import store from '@/store/store';
import ApiService from '@/services/api.service';
import SocketService from '@/services/socket.service';
import AppError from '@/services/error.service';
import { CONNECT, USER_TYPE } from '@/app.config';
import { validateInstallations, validateInstallation } from '@/models/installations.model';
import { sanitizeDevice } from '@/models/devices.model';

const InstallationService = {
  /**
   * Obtiene todas las instalaciones de un usuario
   *
   * @return {Array[Installation]} - Datos de la instalación parseados y validados
   */
  async getInstallations() {
    // Request
    const resource = `installations/${CONNECT.SCOPE}`;
    const response = await ApiService.get(resource);

    // Check model
    const installations = await validateInstallations(response.data);

    // Response
    return installations;
  },

  /**
   * Guarda los datos de una instalación nueva
   *
   * 1- Valido los datos de la instalación
   * 2- Conecto la nueva instalación por socket
   * 3- Añado la instalación al Store
   *
   * @param {Object} data - Datos de la instalación
   * @return {String} - Devuelve el Id de la instalación
   */
  async saveInstallation(data) {
    // Request
    const resource = `installations/${CONNECT.SCOPE}`;
    const response = await ApiService.post(resource, data);

    // Check model
    const installationID = response.data.installationId;
    data.type = USER_TYPE.ADVANCED;
    const installation = await validateInstallation(data); // [1]
    installation.schedules = {};
    installation.scenes = {};
    installation.scenesOrder = [];

    // Conecta la nueva instalación al socket
    // y salva los datos en el store
    await SocketService.connectInstallationSocket(installationID); // [2]
    store.dispatch('unitsModule/saveInstallation', { installationID, data: installation }); // [3]

    // Response
    return installationID;
  },

  /**
   * Actualiza los datos de una instalación
   *
   * @param {Object} data - Datos de la instalación
   * @return {String} - Devuelve el Id de la instalación
   */
  async updateInstallation(data) {
    const installationID = data.id;

    // Request
    if (!store.getters['globalModule/isDemo']) {
      const resource = `installations/${installationID}/${CONNECT.SCOPE}`;
      await ApiService.patch(resource, data);
    }

    // Actualizo los datos en el store
    store.dispatch('unitsModule/updateInstallation', { installationID, data });

    // Response
    return installationID;
  },

  /**
   * Elimina los datos de una instalación
   *
   * @param {String} installationID - Id de la instalación
   * @return {String} - Devuelve el Id de la instalación
   */
  async deleteInstallation(installationID) {
    // Request
    if (!store.getters['globalModule/isDemo']) {
      const resource = `installations/${installationID}/${CONNECT.SCOPE}`;
      await ApiService.delete(resource);
    }

    // Desconecta el socket de la instalación
    // y actualiza los datos en el store
    SocketService.disconnectInstallation(installationID);
    store.dispatch('unitsModule/deleteInstallation', installationID);

    // Response
    return installationID;
  },

  /**
   * Guarda los datos de un dispositivo nuevo
   *
   * @param {Object} data - Datos del dispositivo
   * @return {String} - Devuelve la mac del dispositivo
   */
  async saveDevice(data) {
    // Request
    const resource = `installations/device/${CONNECT.SCOPE}`;
    await ApiService.post(resource, data);

    // Check model
    const deviceMac = data.mac;
    const installationID = data.installation_id;
    const device = sanitizeDevice(data); // [1]

    console.log('GUARDANDO DEVICE----->', data, installationID);

    // Ssalva los datos en el store
    store.dispatch('unitsModule/saveDevice', { installationID, deviceMac, data: device }); // [2]

    // Response
    return deviceMac;
  },

  /**
   * Actualiza los datos de un dispositivo
   *
   * @param {Object} data - Datos del dispositivo
   * @return {String} - Devuelve la mac del dispositivo
   */
  async updateDevice(data) {
    // Request
    if (!store.getters['globalModule/isDemo']) {
      const resource = `installations/device/${CONNECT.SCOPE}`;
      await ApiService.patch(resource, data);
    }

    const deviceMac = data.mac;
    const installationID = data.installation_id;

    // Actualizo los datos en el store
    store.dispatch('unitsModule/updateDevice', { installationID, deviceMac, data });

    // Response
    return deviceMac;
  },

  /**
   * Elimina los datos de un dispositivo
   *
   * @param {Object} data - Datos del dispositivo
   * @return {String} - Devuelve la mac del dispositivo
   */
  async deleteDevice(data) {
    // Request
    if (!store.getters['globalModule/isDemo']) {
      const resource = `installations/device/${CONNECT.SCOPE}`;
      await ApiService.delete(resource, data);
    }

    // Actualiza los datos en el store
    const installationID = data.installation_id;
    const deviceMac = data.mac;
    store.dispatch('unitsModule/deleteDevice', { installationID, deviceMac }); // [1]

    // Response
    return deviceMac;
  },

  /**
   * Libera un dispositivo
   *
   * @param {Object} data - Datos del dispositivo
   * @return {String} - Devuelve la mac del dispositivo
   */
  async releaseDevice(data) {
    // Request
    const resource = `installations/device/${CONNECT.SCOPE}`;
    await ApiService.delete(resource, data);

    // Response
    return data.mac;
  },

  /**
   * Obtiene un listado con todas las timezones
   */
  async getTimezones() {
    // Request
    const resource = 'installations/timezoneIds';
    const response = await ApiService.get(resource);

    const timezones = response.data;

    // Response
    return timezones;
  },

  /*
   * Comprueba si el usuario tiene el dispositivo asociado
   *
   * Si obtenemos el error 'deviceAssociated', el dispositivo está asociado al usuario
   * Si obtenemos el error 'deviceNotConnected', el dispositivo no está conectado al cloud
   *
   * @return {boolean} - Indica si el dispositivo está asociado al usuairo
   * @throw {deviceNotFound} - El dispositivo no está aprovisionado
   * @throw {error} - Otros errores
   */
  async isDeviceAssociated(data) {
    try {
      // Request
      const resource = `devices/checkFreeDevice/${CONNECT.SCOPE}`;
      await ApiService.post(resource, data);

      return false;
    } catch (error) {
      /**
       * Comprueba si el dispositivo está asociado
       */
      if (error.response.data && error.response.data._id === 'deviceAssociated') {
        return true;
      }

      /**
       * Comprueba si el dispositivo está conectado al cloud
       * Sino lo está no puede ser asociado
       */
      if (error.response.data && error.response.data._id === 'deviceNotConnected') {
        return false;
      }

      /**
       * Otros errores
       */
      throw new AppError(error.response.data._id, error.response.statusText);
    }
  },

  /**
   * Check Pin of device
   *
   * @return {boolean} - Success check pin device
   * @throw {error}    - Error when check pin device
   */
  async checkPin(data) {
    // Request
    const resource = `devices/checkValidDevice/${CONNECT.SCOPE}`;
    await ApiService.post(resource, data);

    // Response
    return true;
  },
};

export default InstallationService;
